/* eslint-disable @nx/enforce-module-boundaries */
import {
   ChunkNavigationErrorWatcher,
   ConfigModule,
   ConfigService,
   ENVIRONMENT,
   GlobalChunkErrorHandler,
   globalChunkErrorHandlerFactory,
   initFaro,
   InternationalisationService,
   LocaleModule,
   LocaleService,
   setFaroUser,
   UNHANDLED_REJECTION_PROVIDER,
} from '@abovo/shared-angular-utils';
import { ACL_TARGET_TIME_REGISTRATION, authenticationInterceptor, AuthModule, isClientUser, isUserLoggedIn, useAclPermission } from '@abovo/shared-auth';
import { CustomCurrencyMaskConfig } from '@abovo/shared-models';
import { AccessibilityService, FeatureFlagService, ThemeV2Service } from '@abovo/shared-services';
import { PasskeyPromptService, SharedUiCoreModule, SplashScreenModule, StarWarsSplashScreenComponent } from '@abovo/shared-ui-components';
import { CustomRouterSerializer, initializeLocalization, PreferencesGuard, provideTours, WebvisCoreModule, WebvisGuard } from '@abovo/webvis';
import {
   appReducers,
   dateTransformInterceptor,
   EmployeesStore,
   LookupValuesModule,
   schemaValidationInterceptor,
   TimeRegistrationStore,
} from '@abovo/webvis-data-access';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { DEFAULT_CURRENCY_CODE, effect, ErrorHandler, inject, Injector, isDevMode, NgModule, provideAppInitializer, untracked } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, TitleStrategy } from '@angular/router';
import { FullRouterStateSerializer, RouterState, RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateService } from '@ngx-translate/core';
import { IntlService } from '@progress/kendo-angular-intl';
import { includeBearerTokenInterceptor, KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { provideEnvironmentNgxCurrency } from 'ngx-currency';
import { NgxMaskDirective, provideEnvironmentNgxMask } from 'ngx-mask';
import { MatomoInitializerService, provideMatomo, withRouter } from 'ngx-matomo-client';
import { provideUiTour } from 'ngx-ui-tour-md-menu';
import { filter, firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { AppTitleStrategy } from './app-title-strategy';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing.module';
import { resolveMatomoConfig } from './matomo-config';

@NgModule({
   declarations: [AppComponent],
   imports: [
      BrowserModule,
      BrowserAnimationsModule,
      AuthModule,
      AppRoutingModule,
      StoreModule.forRoot(appReducers, {
         runtimeChecks: {
            strictStateImmutability: false,
            strictActionImmutability: false,
         },
      }),
      StoreRouterConnectingModule.forRoot({
         serializer: FullRouterStateSerializer,
         routerState: RouterState.Minimal,
      }),
      StoreDevtoolsModule.instrument({ connectInZone: true }),
      ConfigModule.forRoot(),
      WebvisCoreModule,
      LookupValuesModule,
      SharedUiCoreModule,
      SplashScreenModule,
      NgxMaskDirective,
      StarWarsSplashScreenComponent,
      LocaleModule,
   ],
   providers: [
      WebvisGuard,
      PreferencesGuard,
      FeatureFlagService,
      // Registers the ngx-ui-tour service (NgxmTourService) and its global defaults. Required because the
      // standalone build no longer provides the service in root. Used by the time-registration guided tour.
      provideUiTour(),
      provideEnvironmentNgxMask(),
      { provide: RouterStateSerializer, useClass: CustomRouterSerializer },
      { provide: TitleStrategy, useClass: AppTitleStrategy },
      { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
      // Outgoing requests apply the interceptors in the order below while responses apply them in reverse order
      // Old link but they did not change it: https://v17.angular.io/guide/http-intercept-requests-and-responses#interceptor-order
      provideHttpClient(withInterceptors([authenticationInterceptor, includeBearerTokenInterceptor, dateTransformInterceptor, schemaValidationInterceptor])),
      {
         provide: ENVIRONMENT,
         useValue: environment,
      },
      provideAppInitializer(() => inject(FeatureFlagService).getConfig()),
      provideAppInitializer(() => {
         const initializerFn = (
            (_accessibilityService: AccessibilityService) => () =>
               Promise.resolve()
         )(inject(AccessibilityService));
         return initializerFn();
      }),
      provideAppInitializer(() => initializeLocalization(inject(LocaleService), inject(TranslateService), inject(IntlService), inject(DateAdapter<Date>))),

      /**
       * On successful authentication: load all employees and show the passkey prompt if applicable.
       */
      provideAppInitializer(() => {
         const keycloak = inject(Keycloak);
         const employeeStore = inject(EmployeesStore);
         const passkeyPromptService = inject(PasskeyPromptService);

         keycloak.onAuthSuccess = () => {
            employeeStore.loadAllEmployees();
            passkeyPromptService.maybeShowPrompt();
         };
      }),

      /**
       * Sets the festive theme if the user is not a client
       * Load client-specific translations if needed
       * Load time-registration timer
       */
      provideAppInitializer(() => {
         const store = inject(Store);
         const themeService = inject(ThemeV2Service);
         const internationalizationService = inject(InternationalisationService);
         const timeRegistrationStore = inject(TimeRegistrationStore);
         const isClient = store.selectSignal(isClientUser);
         const timeRegistrationPermission = useAclPermission()({
            target: ACL_TARGET_TIME_REGISTRATION,
            operation: 'create',
            description: 'Load time registration timer',
         });

         const isLoggedIn = firstValueFrom(store.select(isUserLoggedIn).pipe(filter(Boolean)));
         isLoggedIn.then(() => {
            /** Load active timer */
            if (timeRegistrationPermission()) {
               timeRegistrationStore.loadActiveTimer();
            }

            /** Load client-specific translations if needed */
            internationalizationService.loadGroupWhen('client', store.select(isClientUser)).subscribe();

            /** Set festive theme if user is not a client */
            if (!isClient()) {
               themeService.currentThemeSettings.set(themeService['setInitialTheme'](true));
            }
         });
      }),

      provideTours(),
      provideEnvironmentNgxCurrency(CustomCurrencyMaskConfig),
      provideMatomo(
         {
            mode: 'deferred',
            runOutsideAngularZone: true,
         },
         withRouter(),
      ),
      provideAppInitializer(() => {
         const matomoInitializer = inject(MatomoInitializerService);
         const configService = inject(ConfigService);
         const matomoConfig = resolveMatomoConfig(configService);
         if (!matomoConfig) {
            // The current hostname has no Matomo config, so we do not track it
            return;
         }
         matomoInitializer.initializeTracker(matomoConfig);
      }),
      // Faro intercepts console messages (errors specifically) and sends them to an alloy instance for logging in Grafana via Loki
      provideAppInitializer(() => {
         if (!isDevMode()) {
            return initFaro();
         }
      }),
      provideAppInitializer(() => {
         // Register user properties from Keycloak with Faro so they show up in the logs
         const keycloak = inject(Keycloak);
         const eventSignal = inject(KEYCLOAK_EVENT_SIGNAL);
         const faroUserEffect = effect(() => {
            const { type } = eventSignal();
            if (type === KeycloakEventType.Ready && keycloak.tokenParsed) {
               untracked(() => {
                  setFaroUser(keycloak.tokenParsed);
                  // we no longer need the effect
                  faroUserEffect.destroy();
               });
            }
         });
      }),
      {
         provide: ErrorHandler,
         useFactory: globalChunkErrorHandlerFactory,
         deps: [Injector],
      },
      { provide: GlobalChunkErrorHandler, useExisting: ErrorHandler },
      UNHANDLED_REJECTION_PROVIDER,
      // Ensure the navigation error watcher is constructed at startup
      provideAppInitializer(() => {
         // The constructor subscribes to router events; no-op function to satisfy initializer
         inject(ChunkNavigationErrorWatcher);
      }),
   ],
   bootstrap: [AppComponent],
   exports: [RouterModule, SharedUiCoreModule],
})
export class AppModule {}
